import { useNavigate } from "react-router";
import ReactEcharts, { EChartsOption } from "echarts-for-react";
import { useAppSelector } from "../../../../app/hooks";
import { getCSSVariableValue } from "../../../../app/contrastSlice";
import { tooltipMark } from "../../shared/reportUtil";
import { STUDENT_WRITING_GENRE_SCORES } from "../../student/performance/PerformanceReport";
import { studentPerformanceLink } from "./PerformanceReport";
import { WriterStandardScore, StandardScore } from "./ScoresFilteredByCourseViewModel";

interface ChartData {
	name: string;
	argumentativeScore: number;
	informativeScore: number;
	narrativeScore: number;
	averageScore: number;
	link: string;
}

function initializeOptions(data: ChartData[], average: number) {
	const { foregroundColor, chartPalette, accentColor } = useAppSelector(
		(state) => state.contrast
	);

	const argumentativeColor = !chartPalette
		? getCSSVariableValue("--color-emerald-300")
		: chartPalette[0];
	const informativeColor = !chartPalette
		? getCSSVariableValue("--color-rose-300")
		: chartPalette[1];
	const narrativeColor = !chartPalette ? getCSSVariableValue("--color-sky-300") : chartPalette[2];

	const option: EChartsOption = {
		textStyle: {
			color: foregroundColor,
			fontFamily: "Montserrat",
			fontWeight: "500",
		},
		grid: {
			containLabel: true,
			left: 10,
			bottom: 25,
		},
		xAxis: {
			type: "value",
			min: 0,
			max: 30,
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
			name: "Average Score",
			nameLocation: "middle",
			nameGap: 30,
			splitLine: {
				show: true,
				lineStyle: {
					color: accentColor,
				},
			},
		},
		yAxis: {
			type: "category",
			data: data.map((d) => d.name).toSorted(),
			inverse: true,
			axisLabel: {
				padding: 25,
				lineHeight: 15,
				interval: 0,
				width: 180,
				overflow: "break",
				hideOverlap: false,
			},
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
			axisPointer: {
				type: "none",
			},
		},
		tooltip: {
			trigger: "axis",
			formatter: (
				params: { axisValue: string; color: string; seriesName: string; value: string }[]
			) => {
				const label = `<div>${params[0].axisValue}</div>`;
				const lineItem = (p: any) => {
					return p.value && p.seriesName != "Class Average"
						? `<div>${tooltipMark(p.color)} ${p.seriesName}: <b>${p.value}</b></div>`
						: ``;
				};
				const lineItems = params.reduce((a: string, v: any) => a + lineItem(v), label);
				const avgLine = `<div>${tooltipMark("red")} Class Average: <b>${average}</b></div>`;
				const content = lineItems + avgLine;

				return content;
			},
			textStyle: {
				fontSize: 12,
			},
		},
		legend: {
			data: [
				"Argumentative",
				"Informative/Explanatory",
				"Narrative",
				{
					name: "Class Average",
					icon: "circle",
				},
			],
			show: true,
			padding: 10,
			itemGap: 20,
			textStyle: {
				color: foregroundColor,
			},
			top: 0,
		},
		animationDurationUpdate: 500,
		series: [
			{
				name: "Argumentative",
				type: "bar",
				data: data.map((d) => ({ value: d.argumentativeScore, link: d.link })),
				itemStyle: {
					color: argumentativeColor,
				},
				barWidth: 18,
				cursor: "pointer",
				markPoint: { silent: true },
				label: {
					show: true,
					position: ["100%", "50%"],
					align: "center",
					verticalAlign: "middle",
					offset: [15, 0],
					fontWeight: "bolder",
					fontSize: 10,
				},
				emphasis: {
					itemStyle: {
						shadowBlur: 5,
					},
				},
			},
			{
				name: "Informative/Explanatory",
				type: "bar",
				data: data.map((d) => ({ value: d.informativeScore, link: d.link })),
				itemStyle: {
					color: informativeColor,
				},
				barWidth: 18,
				cursor: "pointer",
				label: {
					show: true,
					position: ["100%", "50%"],
					align: "center",
					verticalAlign: "middle",
					offset: [15, 0],
					fontWeight: "bolder",
					fontSize: 10,
				},
				emphasis: {
					itemStyle: {
						shadowBlur: 5,
					},
				},
			},
			{
				name: "Narrative",
				type: "bar",
				data: data.map((d) => ({ value: d.narrativeScore, link: d.link })),
				itemStyle: {
					color: narrativeColor,
				},
				barWidth: 18,
				cursor: "pointer",
				label: {
					show: true,
					position: ["100%", "50%"],
					align: "center",
					verticalAlign: "middle",
					offset: [15, 0],
					fontWeight: "bolder",
					fontSize: 10,
				},
				emphasis: {
					itemStyle: {
						shadowBlur: 3,
					},
				},
			},
			{
				name: "Class Average",
				type: "line",
				color: "red",
				symbol: "none",
				markLine: {
					name: "Class Average Line",
					data: [
						{
							xAxis: average,
						},
					],
					label: {
						show: false,
					},
					lineStyle: {
						color: "red",
					},
					symbol: ["circle", "circle"],
					silent: true,
				},
			},
		],
	};
	return option;
}

export function GenreScoreChart(props: {
	className: string;
	courseId: number;
	writerData: WriterStandardScore[];
	classData: StandardScore;
}) {
	const navigate = useNavigate();

	const data: ChartData[] = props.writerData.map((d) => ({
		name: d.writerName,
		argumentativeScore: d.argumentativeScore,
		informativeScore: d.informativeScore,
		narrativeScore: d.narrativeScore,
		averageScore: d.averageScore,
		link: studentPerformanceLink(props.courseId, d.writerId, STUDENT_WRITING_GENRE_SCORES),
	}));

	const options = initializeOptions(data, props.classData.averageScore);
	const chartHeight = data.length * 80 + 120;

	const onChartClick = (params: any) => {
		if (params.data.link) navigate(params.data.link);
	};

	if (!data || data.length == 0) return "";

	return (
		<ReactEcharts
			option={options}
			className={`w-full ${props.className}`}
			style={{
				height: chartHeight,
			}}
			onEvents={{
				click: onChartClick,
			}}
		/>
	);
}
